import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@/components/Icon'
import { Markdown } from '@/components/Markdown'
import { splitRationale } from '@/lib/rationale'
import { fmtDateTime, fmtTime } from '@/lib/format'
import type { CanonicalEvent } from '@/lib/runs'

// Playback is a review of ordered observations, not a reconstruction of wall-clock run time.
const STEP_SECONDS = 1

/** The frame and reasoning always come from the same event, in playback and step mode. */
export function RolloutPlayer({
  events,
  videoUrl,
  active,
  live = false,
  hasMore = false,
  loadingMore = false,
  loadMore,
  onStaleImage,
}: {
  events: CanonicalEvent[]
  videoUrl?: string
  active: boolean
  /** Follow arriving frames until the viewer pauses or selects an earlier step. */
  live?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  loadMore?: () => void
  /** Refresh expired screenshot URLs without changing the stored rollout. */
  onStaleImage?: () => void
}) {
  const failedUrls = useRef(new Set<string>())
  const videoPosition = useRef(0)
  const refreshFailed = useCallback((url: string | undefined, key: string) => {
    if (!url || failedUrls.current.has(key)) return
    failedUrls.current.add(key)
    onStaleImage?.()
  }, [onStaleImage])
  const video = useRef<HTMLVideoElement>(null)
  const screen = useRef<HTMLDivElement>(null)
  const body = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<'video' | 'steps'>('video')
  const [position, setPosition] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [following, setFollowing] = useState(live)
  const [speed, setSpeed] = useState(1)
  const [mediaError, setMediaError] = useState(false)
  const [imageError, setImageError] = useState(false)
  const duration = events.length * STEP_SECONDS
  const latestPosition = Math.max(0, duration - STEP_SECONDS)
  const playbackPosition = following ? latestPosition : position
  const requestedIndex = Math.min(Math.floor(playbackPosition / STEP_SECONDS), Math.max(0, events.length - 1))
  const [index, setIndex] = useState(0)
  const requestedUrl = events[requestedIndex]?.screenshot?.url
  const step = events[index]
  const ended = playbackPosition >= duration
  // Keep the last frame when a live job finishes; do not jump back to its start.
  useEffect(() => {
    if (!live && following) {
      setPosition(latestPosition)
      setFollowing(false)
    }
  }, [live, following, latestPosition])
  useEffect(() => {
    let cancelled = false
    function showFrame(failed = false) {
      if (cancelled) return
      setIndex(requestedIndex)
      setImageError(failed)
      if (failed) refreshFailed(requestedUrl, `frame:${requestedIndex}`)
      else failedUrls.current.delete(`frame:${requestedIndex}`)
    }
    if (requestedUrl) {
      // Keep the current image and reasoning visible until the next frame can paint.
      const image = new Image()
      image.src = requestedUrl
      void image.decode().then(() => showFrame(), () => showFrame(true))
    } else {
      showFrame()
    }
    return () => { cancelled = true }
  }, [requestedIndex, requestedUrl, refreshFailed])
  useEffect(() => {
    body.current?.scrollTo(0, 0)
  }, [index, mode])
  useEffect(() => {
    if (!active || mode === 'steps') {
      setPlaying(false)
      video.current?.pause()
    }
  }, [active, mode])
  useEffect(() => {
    if (!playing || following || !active || mode !== 'video' || ended || requestedIndex !== index) return
    let last = performance.now()
    const timer = window.setInterval(() => {
      const now = performance.now()
      const elapsed = ((now - last) / 1000) * speed
      last = now
      setPosition((current) => Math.min(duration, current + elapsed))
    }, 50)
    return () => window.clearInterval(timer)
  }, [playing, following, active, mode, ended, duration, speed, requestedIndex, index])
  useEffect(() => {
    if (ended) setPlaying(false)
  }, [ended])
  useEffect(() => {
    const pause = () => {
      if (document.hidden) {
        setPlaying(false)
        video.current?.pause()
      }
    }
    document.addEventListener('visibilitychange', pause)
    return () => document.removeEventListener('visibilitychange', pause)
  }, [])
  useEffect(() => {
    if (video.current) video.current.playbackRate = speed
  }, [speed])
  useEffect(() => {
    // Warm the next observation without fetching the whole screenshot history at once.
    const url = events[index + 1]?.screenshot?.url
    if (url) {
      const image = new Image()
      image.src = url
    }
  }, [events, index])
  const { rationale, blocks } = splitRationale(step?.rationale)
  const actions = step?.executed_actions?.length ? step.executed_actions : (step?.proposed_actions ?? [])
  function selectStep(next: number) {
    if (next < 0 || next >= events.length) return
    setFollowing(false)
    setPlaying(false)
    setPosition(next * STEP_SECONDS)
  }
  function togglePlayback() {
    if (following) {
      selectStep(index)
      return
    }
    if (ended) setPosition(0)
    setPlaying((current) => !current)
  }
  return (
    <div className="rollout-player" onKeyDown={event => {
      if ((event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      selectStep(index + (event.key === 'ArrowRight' ? 1 : -1))
    }}>
      <div className="rollout-playback">
        <div className="rollout-player-toolbar">
          <div className="rollout-mode" role="group" aria-label="Playback view">
            <button aria-pressed={mode === 'video'} onClick={() => setMode('video')}>
              Video
            </button>
            <button
              aria-pressed={mode === 'steps'}
              onClick={() => { selectStep(index); setMode('steps') }}
              disabled={!events.length}
            >
              Steps
            </button>
          </div>
          {live && <button className="rollout-follow" aria-pressed={following}
            onClick={() => {
              if (following) selectStep(index)
              else { setFollowing(true); setPlaying(false) }
            }}>
            <span aria-hidden="true" />{following ? 'Live' : 'Follow live'}
          </button>}
          {mode === 'video' && (
            <div className="rollout-speeds" role="group" aria-label="Playback speed">
              {[0.5, 1, 1.5, 2].map((rate) => (
                <button
                  key={rate}
                  aria-label={`${rate}× speed`}
                  aria-pressed={speed === rate}
                  onClick={() => setSpeed(rate)}
                >
                  {rate}×
                </button>
              ))}
            </div>
          )}
          {mode === 'steps' && <span className="small muted">Recorded screenshots</span>}
        </div>
        <div ref={screen} className="rollout-screen">
          {step ? (
            step.screenshot?.url && !imageError ? (
              <img
                src={step.screenshot.url}
                alt={`What the agent saw at step ${step.sequence}`}
                onError={() => { setImageError(true); refreshFailed(step.screenshot?.url, `frame:${index}`) }}
              />
            ) : (
              <p className="muted">{imageError ? 'Screenshot unavailable.' : 'No screenshot available for this step.'}
                {imageError && onStaleImage && <button className="link" onClick={() => {
                  failedUrls.current.clear(); onStaleImage()
                }}>Retry</button>}
              </p>
            )
          ) : videoUrl ? (
            <video
              ref={video}
              className="review-video"
              controls
              playsInline
              preload="metadata"
              src={videoUrl}
              onLoadedMetadata={(event) => {
                event.currentTarget.playbackRate = speed
                event.currentTarget.currentTime = videoPosition.current
                setMediaError(false)
                failedUrls.current.delete('video')
              }}
              onTimeUpdate={(event) => {
                if (event.currentTarget.readyState >= 1) videoPosition.current = event.currentTarget.currentTime
              }}
              onError={() => { setMediaError(true); refreshFailed(videoUrl, 'video') }}
            />
          ) : (
            <p className="muted">No recording available.</p>
          )}
          {mediaError && !step && <p className="rollout-media-error">Recording unavailable.</p>}
          {mode === 'video' && step && (
            <div className="rollout-video-controls">
              <button
                aria-label={playing || following ? 'Pause playback' : ended ? 'Replay rollout' : 'Play rollout'}
                onClick={togglePlayback}
                disabled={hasMore}
              >
                <Icon name={playing || following ? 'pause' : 'play'} />
              </button>
              <input
                type="range"
                aria-label="Playback position"
                aria-valuetext={`Step ${index + 1} of ${events.length}`}
                min={0}
                max={duration}
                step={0.01}
                value={playbackPosition}
                onChange={(event) => { setFollowing(false); setPosition(Number(event.target.value)) }}
              />
              <span>
                {index + 1} / {events.length}
              </span>
              <button
                aria-label="Full screen"
                onClick={() => {
                  void screen.current?.requestFullscreen?.().catch(() => {})
                }}
              >
                <Icon name="expand" />
              </button>
            </div>
          )}
        </div>
      </div>
      <section className="rollout-reasoning" aria-label="Agent steps">
        <div className="rollout-step-toolbar">
          <strong>
            {step ? `Step ${index + 1} of ${events.length}${hasMore ? '+' : ''}` : 'Agent steps'}
          </strong>
          {mode === 'steps' && (
            <div className="rollout-step-buttons">
              <button
                className="ghost sm"
                aria-label="Previous step"
                disabled={index === 0}
                onClick={() => selectStep(index - 1)}
              >
                <Icon name="arrow" style={{ transform: 'rotate(180deg)' }} />
              </button>
              <button
                className="ghost sm"
                aria-label="Next step"
                disabled={index >= events.length - 1}
                onClick={() => selectStep(index + 1)}
              >
                <Icon name="arrow" />
              </button>
            </div>
          )}
        </div>
        {mode === 'steps' && events.length > 1 && (
          <input
            className="rollout-step-slider"
            type="range"
            aria-label="Step"
            aria-valuetext={`Step ${index + 1} of ${events.length}`}
            min={0}
            max={events.length - 1}
            value={index}
            onChange={(event) => selectStep(Number(event.target.value))}
          />
        )}
        <div ref={body} className="rollout-step-body">
          <h4>Agent reasoning</h4>
          {rationale ? (
            <Markdown source={rationale} />
          ) : (
            <p className="muted small">No reasoning was recorded for this step.</p>
          )}
          {(actions.length > 0 || blocks.length > 0) && (
            <details className="rollout-action">
              <summary>{step?.executed_actions?.length ? 'Action taken' : 'Proposed action'}</summary>
              <div
                className="rollout-action-code"
                tabIndex={0}
                role="region"
                aria-label="Action details"
              >
                {actions.length ? (
                  <pre>{JSON.stringify(actions, null, 2)}</pre>
                ) : (
                  blocks.map((block, i) => <pre key={i}>{block.code}</pre>)
                )}
              </div>
            </details>
          )}
          {(step?.rejection || step?.failure) && (
            <p className="note warn small">{step.rejection || step.failure}</p>
          )}
          {step && <p className="small muted">
            <time dateTime={step.event_at} title={fmtDateTime(step.event_at)}>{fmtTime(step.event_at)}</time>
            {step.status && <> · {step.status}</>}
            {step.token_total && <> · {step.token_total.input.toLocaleString()} in / {step.token_total.output.toLocaleString()} out
              {step.token_total.reasoning ? ` / ${step.token_total.reasoning.toLocaleString()} reasoning` : ''} so far</>}
          </p>}
        </div>
        {hasMore && (
          <button className="ghost sm" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? 'Loading…' : 'Retry loading steps'}
          </button>
        )}
      </section>
    </div>
  )
}
